import React, { useContext, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { array, object, string } from 'prop-types'
import { flattenArray, usePastScrollYMark } from '@jelly/utils'
import Media from 'react-media'
import { JsonLd } from 'react-schemaorg'

// import components
import {
	AdsenseContext,
	ArticleSchema,
	BackToTop,
	BackToTopContainer,
	BackToTopMobile as BackToTopMobileSVG,
	DfpAdUnitSetup,
	ItemListSchema,
	MeatballMenu,
	Layout,
	SimpleHeader,
} from '@jelly/components'
import { BestPicks } from './BestPicks'
import { ContentBody } from './ContentBody'
import {
	SECTION_BEST_PICKS,
	SECTION_BUYING_GUIDE,
	SECTION_CONTAINER,
	SECTION_REVIEWS,
	SECTION_SERVICES,
} from '../constants'

export const SCROLL_Y_MARK = 1600

const MegaBestPicksWrapper = styled.div`
	width: 100%;
`
const BackToTopWeb = styled(BackToTopContainer)`
	width: 135px;
	right: 0;
	display: none;
	z-index: 1;
	${({ theme }) => theme.MQ.XL} {
		display: ${({ isPastY }) => (isPastY ? 'inherit' : 'none')};
	}
`

export const BackToTopMobile = styled(BackToTopContainer)`
	right: 5px;
	bottom: 5%;
	padding: 10px 15px 0 0;
	border-radius: 4px;
	background-color: ${({ theme }) => theme.backToTop.mainColor};
	opacity: 0.9;
	z-index: 1;
	box-shadow: 0 1px 8px 0 rgba(0, 0, 0, 0.08);
	display: ${({ isPastY }) => (isPastY ? 'inherit' : 'none')};
	:hover {
		background-color: ${({ theme }) => theme.backToTop.hoverColor};
		path,
		text {
			fill: ${({ theme }) => theme.colors.white};
		}
	}

	${({ theme }) => theme.MQ.XL} {
		display: none;
	}
`

MegaBestPicks.propTypes = {
	author: object.isRequired,
	breadcrumbs: object.isRequired,
	canonical: string.isRequired,
	channels: object.isRequired,
	docType: string.isRequired,
	guid: string.isRequired,
	displayModified: string.isRequired,
	displayModifiedText: string.isRequired,
	meta: object.isRequired,
	published: string.isRequired,
	sections: array.isRequired,
	slug: string.isRequired,
	structuredData: object,
	subtitle: string.isRequired,
	template: string.isRequired,
	thumbnail: object.isRequired,
	title: string.isRequired,
	tocElements: array,
	widgetId: string,
}

/**
 * This is the Mega Best Picks index, strings together the MBP template
 *
 * @param {Object} props.author :: author data
 * @param {String} props.canonical :: url path of the current page
 * @param {Object} props.breadcrumbs :: breadcrumb data (primary and optional secondary)
 * @param {Object} props.channels :: DFP adn related rail data (primary and optional secondary)
 * @param {String} props.docType :: the Category doctype used for DFP
 * @param {String} props.guid :: unique id assigned to category
 * @param {String} props.modified :: date saved from CCM
 * @param {String} props.lastModifiedText :: text last modified description saved from CCM
 * @param {Object} props.meta :: meta data for the page
 * @param {String} props.published :: date the content was creatd + published
 * @param {Array} props.sections :: list of sections to display
 * @param {String} props.slug :: Category slug
 * @param {Object} props.structureData :: Schema data for google structured data
 * @param {String} props.subtitle :: name of the category, set in CCM
 * @param {String} props.template :: name of the template, set in CCM
 * @param {Object} props.thumbnail :: data for the hero banner
 * @param {String} props.title :: heading for the MBP page
 * @param {Array} props.tocElementsMbp :: data for the Table of Contents component
 * @param {String} props.widgetId :: id for the widget set for this category
 */

const MAX_TOP_CARD = 5

function clickToCallCallback(services, setServices) {
	const goClickToCallMap = window.goClickToCallMap
	if (goClickToCallMap) {
		const servicesWithPhoneNumber = services.reduce((newServices, currentService) => {
			const phoneNumber = goClickToCallMap[currentService.serviceName]
			if (phoneNumber) {
				currentService.phoneNumber = phoneNumber
			}
			newServices.push(currentService)
			return newServices
		}, [])

		setServices(servicesWithPhoneNumber)
	}
}

export function MegaBestPicks({
	author,
	breadcrumbs,
	canonical,
	channels,
	docType,
	guid,
	displayModified,
	displayModifiedText,
	meta,
	published,
	sections,
	slug,
	structuredData,
	subtitle,
	thumbnail,
	title,
	tocElements,
	widget,
}) {
	const sectionBestPicks = sections.find((section) => section.type === SECTION_BEST_PICKS)
	const [services, setServices] = useState(sections.find((section) => section.type === SECTION_SERVICES)?.services)

	const {
		widget: { widgetId },
	} = widget

	const { adsenseMediaQuery, hasAdsense } = useContext(AdsenseContext)

	useEffect(() => {
		let existingHideEnd = window.dataLayer && window.dataLayer.hide && window.dataLayer.hide.end
		if (existingHideEnd) {
			window.dataLayer.hide.end = () => {
				clickToCallCallback(services, setServices)
				existingHideEnd()
			}
		} else {
			clickToCallCallback(services, setServices)
		}
	}, [services, setServices])

	// Backend should always return a mega best picks top card limit which defaults to 5 if a number is not set.
	// In the case it is undefined, front end should set top card limit to 5.
	const megaBestPicksTopCardLimit = sectionBestPicks?.megaBestPicksTopCardLimit || MAX_TOP_CARD

	// Spreads service data instead of providing just the serviceIds for Best Pick Section
	// adds parameters for anchor tags and service id
	// massages review data to include proper review content based on isJumpLink replaces fullReview and fullReviewTest
	const listOfBestPickServices = flattenArray([
		...sectionBestPicks.bestpicks.map(
			({ descriptionTest, description, isJumpLink, review, reviewUrl, serviceName, ...rest }, index) => {
				review = !!review ? review : {}
				reviewUrl = !!reviewUrl ? reviewUrl : null
				const { thumbnail, canonical, displayName = '', pricing, ...restOfReviewObj } = review

				const restOfReviewObjReview =
					restOfReviewObj?.review && restOfReviewObj?.review !== null ? restOfReviewObj.review : {}
				const { pros, cons, fullReview, fullReviewTest } = restOfReviewObjReview

				// If reviewUrl (override url) has a value, use reviewUrl ( Read Review > ) and set isJumpLink = false
				// else if isJumpLink = true, set review path to anchortag
				// else, set review path to canonical of service review
				const reviewPath = reviewUrl
					? reviewUrl
					: isJumpLink
					? `#${displayName.replace(/\s/g, '-')}`
					: canonical

				const isJumpLinkEnabled = !reviewUrl && isJumpLink

				return {
					...rest,
					...review,
					serviceName: displayName,
					isJumpLink: isJumpLinkEnabled,
					categoryWidgetId: widgetId,
					reviewPath,
					thumbnail: thumbnail?.path,
					description,
					pricing,
					review: {
						...restOfReviewObj,
						pros,
						cons,
						reviewContent: isJumpLinkEnabled ? fullReviewTest : descriptionTest || description || '',
						hasReviewEnabled: isJumpLinkEnabled || !!fullReview, // takes care of non-jump link services WITH NO review page enabled
					},
					topCard: megaBestPicksTopCardLimit > index,
				}
			}
		),
	])

	// Massaging the sections data so that we have everything we need in one array
	//TODO useEffect
	sections = sections.map((section) => {
		if (section.services) {
			section =
				section.type === SECTION_SERVICES
					? { ...section, services: services }
					: { ...section, services: listOfBestPickServices }
		} else if (section.reviews) {
			section.reviews = listOfBestPickServices
		}
		return section
	})

	// Prepare the schema content for each category type
	// Sections: content containers, buying-guides, reviews and best picks
	const schemaContent = sections.map((section) => {
		const { type } = section

		switch (type) {
			case SECTION_REVIEWS:
				const reviewContent = listOfBestPickServices.map((bestpick) => {
					const pro =
						bestpick.review.pros && !!bestpick.review.pros.length
							? `Pros: ${bestpick.review.pros.join(' ')} `
							: ''
					const cons =
						bestpick.review.cons && !!bestpick.review.cons.length
							? `Cons: ${bestpick.review.cons.join(' ')} `
							: ''

					return `${bestpick.serviceName} ${bestpick.fullTitle} ${pro} ${cons} ${bestpick.review.reviewContent} `
				})
				return reviewContent
			case SECTION_BUYING_GUIDE:
				return section.containers.length ? section.containers.map((sect) => sect.html) : ''
			case SECTION_CONTAINER:
				return section.container.html
			default:
				return ''
		}
	})

	const isScrolledPastY = usePastScrollYMark(SCROLL_Y_MARK)

	// pass along default meta, robots: 'index, follow' and
	// pass along sections within ArticleSchema
	return (
		<MegaBestPicksWrapper id="top-of-page">
			<Layout meta={meta} inverseLinkColors={true} hasAdDisclosure={true}>
				<JsonLd
					item={ArticleSchema({
						thumbnail,
						content: schemaContent.join(' '),
						author,
						canonical,
						channels,
						lastModified: displayModified,
						breadcrumbs,
						published,
						title,
					})}
				/>
				<JsonLd
					item={ItemListSchema({
						authorName: author.name,
						canonical,
						items: listOfBestPickServices,
						modifiedDate: displayModified,
						publishedDate: published,
						title,
					})}
				/>
				{structuredData && <JsonLd item={structuredData} />}
				<MeatballMenu elements={tocElements} />
				<div>
					<SimpleHeader
						author={author}
						breadcrumbs={breadcrumbs}
						canonical={canonical}
						date={displayModified}
						imageSrc={thumbnail}
						published={published}
						shouldShowLeaderboardAds={true}
						showOverlappingContent={true}
						streamText={meta.description}
						title={title}
					/>
					{/* There will be no more bottom best pick cards but those services will be displayed lower on the page so only sending in top cards now to BestPicks */}
					<BestPicks services={listOfBestPickServices.filter((service) => service.topCard)} />
				</div>
				<ContentBody
					author={author}
					categoryWidgetId={widgetId}
					displayModified={displayModified}
					displayModifiedText={displayModifiedText}
					docType={docType}
					sections={sections}
					slug={slug}
					streamText={meta.description}
					subtitle={subtitle}
					thumbnail={thumbnail}
					tocElements={tocElements}
				/>
				<BackToTopWeb isPastY={isScrolledPastY}>
					<BackToTop />
				</BackToTopWeb>
				<BackToTopMobile isPastY={isScrolledPastY}>
					<BackToTopMobileSVG />
				</BackToTopMobile>
				{hasAdsense && (
					<Media query={adsenseMediaQuery} defaultMatches={false}>
						{(matches) => matches && <DfpAdUnitSetup channels={channels} docType={docType} guid={guid} />}
					</Media>
				)}
			</Layout>
		</MegaBestPicksWrapper>
	)
}
